import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Edit, Plus, Trash2, Loader } from 'lucide-react';
import useSWR from 'swr';

import NewOfferService from '../../services/promotion-offer-service';
import { useAuth } from '../../contexts/AuthContext';
import LinkImg from "../../assets/images/link.svg";

import { STATUS_MESSAGES } from '../../utils/api-handlers/http-status-code';
import { showToast } from '../../components/ToastConfig';

const frontendUrl = process.env.REACT_APP_FRONTEND_URL;

const STATUS_OPTIONS = [
  { value: '', label: '不限' },
  { value: 'active', label: '啟用' },
  { value: 'inactive', label: '停用' },
];

const SORTBY_OPTIONS = [
  { value: 'id', label: 'ID' },
  { value: 'validFrom', label: '起始日' },
  { value: 'validTo', label: '截止日' },
  { value: 'maxUsage', label: '可使用次數' },
  { value: 'createdAt', label: '建立日期' },
];

const SORT_OPTIONS = [
  { value: 'asc', label: '由小到大' },
  { value: 'desc', label: '由大到小' },
];

// SWR fetcher function
const fetcher = async ([url, keyword, status, from, to, sortBy, sort, page]) => {
  try {
    // Transform URL parameters to API parameters
    const apiParams = {
      search: keyword,
      isEnabled: status === 'inactive' ? false : status === 'active' ? true : null,
      validFrom: from ? from?.replaceAll("-", "/") : "",
      validTo: to ? to?.replaceAll("-", "/") : "",
      sortOrder: sortBy || "id",
      sortDirection: sort === 'asc' ? 'Ascending' : 'Descending',
      page
    };
    const res = await NewOfferService.search(apiParams, page);
    return res.data;
  } catch (error) {
    if (error.response?.status === 400 && ["1002", "8000"].includes(error.response?.data?.errorCode)) {
      return { data: [], pagination: { totalCount: 0 } };
    }
    throw error;
  }
};

function PromotionOfferList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('keyword') || '');
  const [pageInput, setPageInput] = useState(searchParams.get('page') || '1');
  const [isComposing, setIsComposing] = useState(false);
  const Navigate = useNavigate();
  const Location = useLocation();
  const { logout } = useAuth();

  // Get search parameters from URL
  const keyword = searchParams.get('keyword') || '';
  const status = searchParams.get('status') || '';
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const sortBy = searchParams.get('sortBy') || 'id';
  const sort = searchParams.get('sort') || 'desc';
  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = 10;

  // Use SWR to fetch data
  const { data, error, isLoading, mutate } = useSWR(
    ['/api/OfferManagement/Search', keyword, status, from, to, sortBy, sort, page],
    fetcher,
    {
      onError: (err) => {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          logout();
        } else if (err.response?.status !== 400) {
          const errorMessage = STATUS_MESSAGES[err.response?.status] || STATUS_MESSAGES["default"];
          showToast.error(errorMessage);
        }
      },
      revalidateOnFocus: false,
    }
  );

  const [offersWithStatus, setOffersWithStatus] = useState([]);
  const offers = data?.data || [];
  const totalItems = data?.pagination?.totalCount || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (offers.length > 0) {
      setOffersWithStatus(offers.map(offer => ({
        ...offer,
        isUpdating: false
      })));
    }
  }, [offers]);

  const handleToggleStatus = async (e, offerId, newStatus) => {
    e.preventDefault();
    e.stopPropagation();

    // Update local state to show loading
    setOffersWithStatus(prev => prev.map(offer => 
      offer.id === offerId 
        ? { ...offer, isUpdating: true }
        : offer
    ));

    const targetOffer = offersWithStatus.find(offer => offer.id === offerId);
    
    if (!targetOffer) {
      console.error('Target offer not found');
      showToast.error('更新狀態失敗，請稍後再試');
      return;
    }

    // const { validFrom, validTo, maxUsage, name } = targetOffer;

    try {
      await NewOfferService.update(offerId, {isEnabled: newStatus});
      mutate(); // Refresh the data
      showToast.success(newStatus ? '優惠券已啟用' : '優惠券已停用');
    } catch (error) {
      console.error('Error updating offer status:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else {
        showToast.error('更新狀態失敗，請稍後再試');
      }

      // Revert local state on error
      setOffersWithStatus(prev => prev.map(offer => 
        offer.id === offerId 
          ? { ...offer, isUpdating: false }
          : offer
      ));
    }
  };

  // Handle search params update
  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
    if (updates.page) {
      setPageInput(updates.page);
    }
  }, [searchParams, setSearchParams]);

  // Search debounce
  useEffect(() => {
    if (isComposing) return;
    if (searchInput === keyword) return;

    const timer = setTimeout(() => {
      updateSearchParams({ keyword: searchInput, page: '1' });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, isComposing, updateSearchParams, keyword]);

  const handleDelete = async (e, offerId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('確定要刪除此優惠券嗎？此操作無法復原。')) {
      return;
    }

    try {
      await NewOfferService.delete(offerId);
      mutate();
      showToast.success('優惠券已刪除');
    } catch (error) {
      console.error('Error removing offer:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else {
        showToast.error('刪除失敗，請稍後再試');
      }
    }
  };
  
  return (
    <div className={`p-6 pt-0 ${totalPages > 1 ? "h-[78vh]" : ""} overflow-auto`}>
      <div className="container mx-auto h-full">
        <div className="sticky top-0 pt-6 pb-4 bg-white z-10">          
          <h1 className="text-2xl font-bold">Promotions</h1>
          <div className='flex items-center mt-5 space-x-7'>
            <Link to="/promotion/offer" className={`${Location.pathname === "/promotion/offer" ? "font-bold underline" : ""}`}>Offer</Link>
            <Link to="/coupon">Discount</Link>
          </div>
          
          {/* Search and filter controls */}
          <div className="space-y-4">
            <div className="flex justify-between mt-4">
              <div className="flex items-center gap-4">
                <Link className='flex items-center gap-2' to="/promotion/offer/new">
                    <Plus size={24} className='border border-gray-600 rounded-full p-1' />
                    <span>Create</span>
                </Link>
              </div>
              {/* <div className="flex gap-4">
                <select
                  value={status || 'placeholder-status'}
                  onChange={(e) => {
                    const value = e.target.value === 'placeholder-status' ? '' : e.target.value;
                    updateSearchParams({ status: value, page: '1' });
                  }}
                  className="px-3 py-2 border rounded"
                >
                  <option value="placeholder-status" disabled hidden>Status</option>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div> */}
            </div>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="text-center py-8 text-gray-500 flex items-center justify-center">
            <Loader className="mr-1 animate-spin w-5 h-5" />
            載入中...
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="text-center py-8 text-red-500">
            載入資料時發生錯誤，請稍後再試
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && offers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {keyword || status || sort ? '沒有符合搜尋條件的優惠券' : '目前沒有任何優惠券'}
          </div>
        )}

        {/* Account list */}
        {!isLoading && !error && offers.length > 0 && (
          <div className="space-y-2 overflow-auto h-[calc(100%-9rem)] relative">
            {/* Table header */}
            <div className="flex items-center p-4 bg-gray-50 rounded-t border-b font-medium justify-between min-w-[1200px] sticky top-0 z-[2]">
              <div className="flex-1 flex">
                <div className="w-[6%]">ID</div>
                <div className="w-[8%]">Status</div>
                <div className="w-[21.5%]">Offer Name</div>
                <div className="w-[11%]">Price</div>
                <div className="w-[7.5%]">Seats</div>
                <div className="w-[9%]">Free Trail (days)</div>
                <div className="w-[12%]">Payment Period (days)</div>
                <div className="w-[9%]">Subscribers</div>
                <div className="w-[10.5%]">Create Time</div>
                <div className="w-[5.5%]">Creator</div>
              </div>
            </div>

            <div className='min-w-[1200px]'>

            {/* Table body */}
            {offers.map(offer => (
              <Link key={offer.id} to={`/promotion/offer/${offer.id}`} className="flex items-center p-4 border rounded hover:bg-gray-50 justify-between">
                <div className="flex-1 flex">
                  <div className="w-[6%] flex items-center">#{offer.id}</div>
                  <div className="w-[8%] flex items-center">{offer.isActive ? "Active" : "Inactive"}</div>
                  <div className="w-[21.5%] flex items-center">
                    <span>{offer.name}</span>
                    <a href={`${frontendUrl}?promo-code=${offer.code}`} rel="noopener noreferrer" target='_blank' className='ml-1.5 mr-3 flex-shrink-0'
                      onClick={(e) => {
                        e.preventDefault(); // 阻止預設行為
                        e.stopPropagation(); // 阻止事件冒泡到外層 Link
                        window.open(`${frontendUrl}?promo-code=${offer.code}`, '_blank', 'noopener,noreferrer'); // 開啟新視窗
                      }
                    }>
                      <img src={LinkImg} className='scale-90'></img>
                    </a>
                  </div>
                  <div className="w-[11%] flex items-center">${offer.amount/100}/month</div>
                  <div className="w-[7.5%] flex items-center">{offer.seatsLimit === 0 ? "unlimited" : offer.seatsLimit}</div>
                  <div className="w-[9%] flex items-center">{offer.trialDays}</div>
                  <div className="w-[12%] flex items-center">{offer.termMonths === 24 ? "730" : offer.termMonths === 12 ? "365" : offer.termMonths === 6 ? "180" : offer.termMonths === 3 ? "90" : offer.termMonths === 0 ? "Unlimited" : offer.termMonths * 30}</div>
                  <div className="w-[9%] flex items-center">{offer.seatsUsed}</div>
                  <div className="w-[10.5%] flex items-center">{offer.createTime?.substring(0,11)}</div>
                  <div className="w-[5.5%] flex items-center">{offer.createUser}</div>
                </div>
              </Link>
            ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-4 fixed bottom-[4%] w-[calc(100%-21rem)] max-w-[1200px]">
            <div className="flex items-center">
              <span className="text-gray-700">Page</span>
              <input
                type="text"
                className="w-16 mx-2 px-2 py-1 border rounded text-center"
                value={pageInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setPageInput(value);
                }}
                onBlur={() => {
                  const pageNum = parseInt(pageInput);
                  if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
                    setPageInput(page.toString());
                  }
                }}
              />
            </div>
          
            {/* Pagination buttons */}
            <div className="flex flex-wrap justify-center gap-2 w-full md:w-auto px-4">
              <button
                onClick={() => updateSearchParams({ page: String(page - 1) })}
                disabled={page === 1}
                className={`px-3 py-1 border rounded ${
                  page === 1 ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                Prev
              </button>
              
              {/* First page */}
              <button
                onClick={() => updateSearchParams({ page: '1' })}
                className={`px-3 py-1 border rounded ${
                  1 === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                }`}
              >
                1
              </button>

              {/* Pagination dots and numbers */}
              {page > 6 && <span className="px-3 py-1">...</span>}

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(pageNum => {
                  const showRange = 4;
                  return pageNum > page - showRange && pageNum < page + showRange;
                })
                .filter(pageNum => pageNum !== 1 && pageNum !== totalPages)
                .map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    className={`px-3 py-1 border rounded ${
                      pageNum === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

              {page < totalPages - 5 && <span className="px-3 py-1">...</span>}

              {/* Last page */}
              {totalPages > 1 && (
                <button
                  onClick={() => updateSearchParams({ page: String(totalPages) })}
                  className={`px-3 py-1 border rounded ${
                    totalPages === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() => updateSearchParams({ page: String(page + 1) })}
                disabled={page === totalPages}
                className={`px-3 py-1 border rounded ${
                  page === totalPages ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PromotionOfferList;